import { useMemo, useState } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  type InfiniteData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { ADMIN_VAULT_COMPANIES_KEY, ADMIN_VAULT_UPLOADS_KEY } from '../admin/hooks';
import {
  amendProduct,
  archiveProduct,
  deleteSupportingDocument,
  getSupportingDocMarkdown,
  getSupportingDocumentLimits,
  getProductForEdit,
  getProducts,
  getSubmissionPathways,
  setSubmissionPathways,
  type SubmissionPathways,
  getEuIdentifiers,
  setEuIdentifiers,
  type EuIdentifiers,
  getProductsPage,
  getProductsStats,
  type ProductsSortKey,
  type ProductTypeFilter,
  listSupportingDocuments,
  restoreProduct,
  uploadProductDocumentDirect,
  uploadSupportingDocumentsDirect,
  type SupportingDocMarkdown,
  type SupportingDocumentLimits,
  type SupportingDocument,
  type ProductEditSnapshot,
  type ProductsPage,
  type UploadProductDocumentArgs,
  type UploadProductDocumentResponse,
} from './api';
import type { UploadProgress } from './upload-batches';

export type { ProductsSortKey, ProductTypeFilter } from './api';

/** True when any agent run is queued/running — OR a tech-spec extraction is still
 *  building — for this product, so the roster keeps polling and flips a
 *  "Processing" badge to "Review Needed" without a manual refresh. */
function hasInFlightRun(p: {
  in_flight_predicate_run_id: number | null;
  in_flight_dossier_run_id: number | null;
  in_flight_strategy_runs: Record<string, number>;
  in_flight_protocol_runs: Record<string, number>;
  in_flight_report_runs: Record<string, number>;
  extraction_status?: 'pending' | 'ready' | 'failed' | 'approved' | null;
  supporting_docs_pending?: number;
  report_status?: string | null;
  predicate_report_status?: string | null;
  protocol_report_status?: string | null;
  csr_report_status?: string | null;
}): boolean {
  return (
    (p.supporting_docs_pending ?? 0) > 0 ||
    p.in_flight_predicate_run_id != null ||
    p.in_flight_dossier_run_id != null ||
    Object.keys(p.in_flight_strategy_runs ?? {}).length > 0 ||
    Object.keys(p.in_flight_protocol_runs ?? {}).length > 0 ||
    Object.keys(p.in_flight_report_runs ?? {}).length > 0 ||
    p.extraction_status === 'pending' ||
    p.report_status === 'checking' ||
    p.predicate_report_status === 'checking' ||
    p.protocol_report_status === 'checking' ||
    p.csr_report_status === 'checking'
  );
}

/**
 * Shared product-roster query used by every list page (Predicate Finder,
 * Regulatory Strategy, Clinical Protocols, Clinical Reports, Submission
 * Dossier, Simulators) and the dashboard. Single source of truth — one
 * fetch across pages.
 */
export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: getProducts,
    staleTime: 30 * 1000,
    retry: 1,
    // Products are customer-scoped (every teammate sees the same list).
    // Refetching on tab focus catches teammate-created products, status
    // transitions, and new agent runs without a hard refresh.
    refetchOnWindowFocus: true,
    // While ANY run is in flight, poll so in-flight indicators (dashboard
    // "Running Now", list-page chips / View Running buttons) self-clear
    // when runs finish — without this, a focused tab never refetches and
    // a completed run looks stuck until a manual refresh. Goes quiet
    // (false) the moment nothing is running.
    refetchInterval: (query) => {
      const products = query.state.data;
      if (!products) return false;
      return products.some(hasInFlightRun) ? 10_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/** Server-side sort state for the products list tables. */
export interface ProductsSort {
  key: ProductsSortKey;
  dir: 'asc' | 'desc';
}

/** The server's natural order — newest first. */
export const DEFAULT_PRODUCTS_SORT: ProductsSort = { key: 'submitted', dir: 'desc' };

/** Header-click behavior: same column flips direction; a new column
 *  starts asc (except submitted, where newest-first is the useful start). */
export function toggleProductsSort(sort: ProductsSort, key: ProductsSortKey): ProductsSort {
  if (sort.key === key) {
    return { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: key === 'submitted' ? 'desc' : 'asc' };
}

/** Filter/sort params shared by the list pages. All server-side — no
 *  loaded-rows-only filtering or sorting anywhere. */
export interface ProductsListParams {
  /** Debounce before passing user keystrokes here. */
  q?: string;
  sort?: ProductsSort;
  eligibleFor?: 'finder';
  /** Agent dispatch tables pass this: only products whose tech-spec fact audit is
   *  approved (or that have no extraction). Keeps un-reviewed products out of run targets. */
  dispatchReady?: boolean;
  /** Narrow to one product type (device / drug / biologic). Server-side, so
   *  the pages and the "Showing X of Y" footer both reflect it. */
  productType?: ProductTypeFilter;
  /** List archived products instead of active ones. */
  archived?: boolean;
}

/**
 * Keyset-paginated products list — drives the agent list tables' "Load
 * more" UX (10 per page by default). Shares the ['products'] key prefix
 * so every existing mutation invalidation refreshes it too. Same
 * poll-while-running behavior as ``useProducts`` (a refetch re-fetches
 * every loaded page). While a NEW filter/sort key loads, the previous
 * rows stay visible (placeholderData) so typing in the search box
 * doesn't flash an empty table.
 */
export function useProductsInfinite(limit = 10, params: ProductsListParams = {}) {
  const q = params.q?.trim() ?? '';
  const sort = params.sort ?? DEFAULT_PRODUCTS_SORT;
  const archived = params.archived ?? false;
  return useInfiniteQuery({
    queryKey: [
      'products',
      'infinite',
      limit,
      q,
      sort.key,
      sort.dir,
      params.eligibleFor ?? null,
      params.dispatchReady ?? false,
      params.productType ?? null,
      archived,
    ],
    queryFn: ({ pageParam }) =>
      getProductsPage({
        limit,
        cursor: pageParam,
        q: q || undefined,
        sort: sort.key,
        dir: sort.dir,
        eligibleFor: params.eligibleFor,
        dispatchReady: params.dispatchReady,
        productType: params.productType,
        archived,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: ProductsPage) => lastPage.nextCursor,
    staleTime: 30 * 1000,
    retry: 1,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      if (!pages) return false;
      return pages.some((page) => page.items.some(hasInFlightRun)) ? 10_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * Per-PAGE load-more view over the shared infinite products cache.
 *
 * The fetched pages are cached once for all list pages (one download),
 * but the DISPLAY depth is local component state — so clicking Load more
 * on one page never expands another page's table, and every page starts
 * back at the first 10 rows on entry. Load more reveals cached rows
 * instantly and only hits the network when the reveal runs past what's
 * cached. Changing any filter/sort param resets the depth to one page.
 */
export function useProductsLoadMore(pageSize = 10, params: ProductsListParams = {}) {
  const query = useProductsInfinite(pageSize, params);
  const sort = params.sort ?? DEFAULT_PRODUCTS_SORT;
  const paramsKey = `${pageSize}|${params.q?.trim() ?? ''}|${sort.key}|${sort.dir}|${params.eligibleFor ?? ''}|${params.dispatchReady ? 'dr' : ''}|${params.productType ?? ''}|${params.archived ? 'arch' : ''}`;
  // Render-time state reset (the React-endorsed "derive state from props"
  // pattern) — a new filter/sort starts back at one page deep.
  const [depth, setDepth] = useState({ paramsKey, visibleCount: pageSize });
  if (depth.paramsKey !== paramsKey) {
    setDepth({ paramsKey, visibleCount: pageSize });
  }
  const visibleCount = depth.paramsKey === paramsKey ? depth.visibleCount : pageSize;
  const loaded = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );
  const visible = loaded.slice(0, visibleCount);
  const hasMore = visibleCount < loaded.length || query.hasNextPage;
  const loadMore = () => {
    // During a filter/sort transition the table shows the PREVIOUS key's
    // rows (placeholderData) — acting on them would page the old cursor
    // under the new key. Ignore clicks until the new result lands.
    if (query.isPlaceholderData) return;
    const next = visibleCount + pageSize;
    setDepth({ paramsKey, visibleCount: next });
    if (next > loaded.length && query.hasNextPage) void query.fetchNextPage();
  };
  return {
    visible,
    hasMore,
    isFetchingMore: query.isFetchingNextPage,
    loadMore,
    isLoading: query.isLoading,
    isError: query.isError,
    /** Rows matching the active filters (X-Total-Count) — null until known. */
    totalCount: query.data?.pages.at(-1)?.totalCount ?? null,
  };
}

/**
 * Devices excluded from the Predicate Finder only because a market's
 * classification is still "Not sure yet" — the finder page's "Awaiting
 * classification" callout. Shares the ['products'] key prefix so
 * classification edits and product mutations refresh it.
 */
export function useFinderAwaitingClassification() {
  return useQuery({
    queryKey: ['products', 'finder-awaiting-classification'],
    // dispatchReady matches the finder table's filter: a device held back by
    // an unreviewed extraction wouldn't appear there even once classified, so
    // it doesn't belong in the callout either.
    queryFn: () =>
      getProductsPage({
        limit: 50,
        eligibleFor: 'finder_awaiting_classification',
        dispatchReady: true,
      }),
    staleTime: 30 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
  });
}

/**
 * Accurate list-page tile counts, independent of pagination. Polls while
 * any agent has running work so the "Running" tile self-clears.
 */
export function useProductsStats() {
  return useQuery({
    queryKey: ['products', 'stats'],
    queryFn: getProductsStats,
    staleTime: 15 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
    // Polls ALWAYS, not only while a run is in flight. Every number here moves
    // for reasons `running` can't see: a teammate registering or archiving a
    // product, and — for the roster sizes — a tech-spec extraction being
    // approved, which is what flips a product into `dispatch_ready`. With the
    // old in-flight-only condition the badge simply sat there through all of
    // that until the window was refocused.
    //
    // 10s while something is running (matching useProducts, so the Running
    // counter and the row chips move together), 30s otherwise. Never in a
    // background tab — refetchIntervalInBackground stays false — so an idle
    // tab costs nothing and refetchOnWindowFocus covers the return.
    refetchInterval: (query) => {
      const stats = query.state.data;
      if (!stats) return false;
      return Object.values(stats.running).some((n) => n > 0) ? 10_000 : 30_000;
    },
    refetchIntervalInBackground: false,
  });
}

// ── Product supporting documents (free-form, many-per-product) ───────────

const supportingDocsKey = (productId: number) =>
  ['product-supporting-documents', productId] as const;

const supportingDocLimitsKey = (productId: number) =>
  ['product-supporting-document-limits', productId] as const;

/**
 * The product's supporting-document quota + current usage, so the uploader can state
 * the limits instead of letting the user discover them by being rejected. Same tier as
 * the document list (customer-scoped, refetch on focus) and invalidated by the upload
 * and delete mutations, since both move `used_files` / `used_bytes`.
 */
export function useSupportingDocumentLimits(productId: number | null) {
  return useQuery<SupportingDocumentLimits>({
    queryKey:
      productId == null
        ? ['product-supporting-document-limits', 'none']
        : supportingDocLimitsKey(productId),
    queryFn: () => {
      if (productId == null) throw new Error('productId is null');
      return getSupportingDocumentLimits(productId);
    },
    enabled: productId != null,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useSupportingDocuments(productId: number | null) {
  return useQuery<SupportingDocument[]>({
    queryKey:
      productId == null
        ? ['product-supporting-documents', 'none']
        : supportingDocsKey(productId),
    queryFn: () => {
      if (productId == null) throw new Error('productId is null');
      return listSupportingDocuments(productId);
    },
    enabled: productId != null,
    staleTime: 0,
    refetchOnWindowFocus: true,
    // A large PDF is stored at once and converted in the background: poll while any
    // row is still "pending" so "Converting…" flips to done without a manual refresh.
    refetchInterval: (query) =>
      query.state.data?.some((d) => d.conversion_status === 'pending') ? 5000 : false,
  });
}

export function useUploadSupportingDocuments(productId: number | null) {
  const queryClient = useQueryClient();
  const refresh = () => {
    if (productId != null) {
      void queryClient.invalidateQueries({ queryKey: supportingDocsKey(productId) });
      // Uploading consumes quota — the displayed "x of y" must move with it.
      void queryClient.invalidateQueries({ queryKey: supportingDocLimitsKey(productId) });
    }
  };
  return useMutation({
    // Browser→storage per file (see upload-direct.ts): the list refreshes after EVERY
    // file, so documents appear as they land instead of all at once at the end.
    mutationFn: async ({
      files,
      onProgress,
    }: {
      files: File[];
      onProgress?: (p: UploadProgress) => void;
    }) => {
      if (productId == null) throw new Error('productId is null');
      return uploadSupportingDocumentsDirect(productId, files, onProgress, refresh);
    },
    onSettled: refresh,
  });
}

/**
 * A product-side mutation that changes a product-derived label (archive/restore/amend, or deleting a
 * promoted supporting doc) must refresh the vault "Linked to X" chip + the admin promote panel's
 * "Promoted -> X" label and company counts, which read the product's live status/name — otherwise they
 * stay stale until a manual browser refresh. Invalidate the specific keys, not ['admin'] wholesale.
 */
function invalidateProductDerivedViews(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['vault'] });
  void qc.invalidateQueries({ queryKey: ADMIN_VAULT_UPLOADS_KEY });
  void qc.invalidateQueries({ queryKey: ADMIN_VAULT_COMPANIES_KEY });
}

export function useDeleteSupportingDocument(productId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (docId: number) => {
      if (productId == null) throw new Error('productId is null');
      return deleteSupportingDocument(productId, docId);
    },
    onSuccess: () => {
      if (productId != null) {
        void queryClient.invalidateQueries({ queryKey: supportingDocsKey(productId) });
        // Deleting frees quota back up.
        void queryClient.invalidateQueries({
          queryKey: supportingDocLimitsKey(productId),
        });
        invalidateProductDerivedViews(queryClient);
      }
    },
  });
}

/** The converted text, in chunks the preview appends on demand ("Show more") — a huge
 *  document's text is never parsed by the browser in one go. */
export function useSupportingDocMarkdown(productId: number | null, docId: number | null) {
  return useInfiniteQuery<SupportingDocMarkdown, Error, InfiniteData<SupportingDocMarkdown>, readonly unknown[], number>({
    queryKey: ['supporting-doc-markdown', productId, docId],
    queryFn: ({ pageParam }) => {
      if (productId == null || docId == null) throw new Error('ids are null');
      return getSupportingDocMarkdown(productId, docId, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    enabled: productId != null && docId != null,
    staleTime: 5 * 60 * 1000, // converted text is immutable for a given doc
  });
}

// ── Product edit / amend / archive (post-submit) ────────────────────────

export const productEditKey = (productId: number) =>
  ['product-edit', productId] as const;

/**
 * Fetch a submitted product's full data for editing. Returns the
 * snapshot the editor needs (data + updated_at). 404 surfaces as an
 * error on the result; callers redirect or show a "no longer
 * available" message.
 */
export function useProductForEdit(productId: number | null) {
  return useQuery<ProductEditSnapshot>({
    queryKey: productId == null ? ['product-edit', 'none'] : productEditKey(productId),
    queryFn: () => {
      if (productId == null) throw new Error('productId is null');
      return getProductForEdit(productId);
    },
    enabled: productId != null,
    staleTime: 0, // always refetch on remount — edit mode wants fresh data
    retry: 0, // 404 should fail fast so the UI can recover
    // The edit page must NOTICE when a teammate archives or deletes the product under it,
    // not just fail the next save: on focus (the "switched back to a stale tab" case, which
    // is exactly how it was reported) and every 15s while it stays open. Per-query override
    // of the global refetchOnWindowFocus:false, which exists for run polling — same
    // precedent as useMe. `retry: 0` above is what lets a 404 (deleted) surface at once.
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
    // Keep polling while the tab is in the BACKGROUND — the teammate case is precisely
    // "I archived it in my tab while yours sat behind it"; by default TanStack pauses the
    // interval for hidden tabs, which is why the notice only appeared after a refresh.
    refetchIntervalInBackground: true,
  });
}

/**
 * Amend mutation. Always pass the current ``ifMatch`` (from the latest
 * snapshot) so the optimistic-concurrency guard runs. The hook updates
 * the cached snapshot with the response (advancing ``ifMatch`` for the
 * next save) and invalidates the products list so amended values appear
 * on the dashboard.
 */
export function useAmendProduct(productId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { data: Record<string, unknown>; ifMatch: string }) => {
      if (productId == null) throw new Error('productId is null');
      return amendProduct(productId, args.data, args.ifMatch);
    },
    onSuccess: (snapshot) => {
      if (productId != null) {
        // MERGE, don't replace: the amend response omits tech_spec_markdown (it never changes
        // it — saves serializing/sending the ~86 KB column each save), so keep the cached copy.
        // Replacing would null it and flip the edit page off the section view.
        queryClient.setQueryData<ProductEditSnapshot>(productEditKey(productId), (old) =>
          old ? { ...old, ...snapshot, tech_spec_markdown: old.tech_spec_markdown } : snapshot,
        );
        void queryClient.invalidateQueries({ queryKey: ['products'] });
        invalidateProductDerivedViews(queryClient);
      }
    },
  });
}

/**
 * Soft-archive a product. Invalidates the products list so the row
 * disappears from the dashboard immediately on success.
 */
export function useArchiveProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) => archiveProduct(productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      invalidateProductDerivedViews(queryClient);
    },
  });
}

/**
 * Un-archive a product. Invalidates the products list so the row moves
 * back from the archived view to the active list on success.
 */
export function useRestoreProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) => restoreProduct(productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      invalidateProductDerivedViews(queryClient);
    },
  });
}

/** Upload a PDF/DOCX tech-spec → deterministic extract → submitted Product.
 *  Invalidates the products list so it refreshes on success. */
export function useUploadProductDocument() {
  const queryClient = useQueryClient();
  // Upload progress fraction (0..1) while a big file streams to S3; null when
  // idle or on the through-app path (which has no per-byte progress).
  const [progress, setProgress] = useState<number | null>(null);
  const mutation = useMutation({
    mutationFn: (args: UploadProductDocumentArgs): Promise<UploadProductDocumentResponse> =>
      uploadProductDocumentDirect(args, setProgress),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onSettled: () => setProgress(null),
  });
  return { ...mutation, progress };
}

// ── Step-2 country / regulatory-classification map ───────────────────────

const submissionPathwaysKey = (productId: number) =>
  ['product-submission-pathways', productId] as const;

const euIdentifiersKey = (productId: number) => ['product-eu-identifiers', productId] as const;

/** The device's EU identifiers (Basic UDI-DI, UDI-DI) — editor tier, always fresh. */
export function useEuIdentifiers(productId: number | null, enabled = true) {
  return useQuery<EuIdentifiers>({
    queryKey: productId == null ? ['product-eu-identifiers', 'none'] : euIdentifiersKey(productId),
    queryFn: () => {
      if (productId == null) throw new Error('productId is null');
      return getEuIdentifiers(productId);
    },
    enabled: productId != null && enabled,
    staleTime: 0,
  });
}

export function useSetEuIdentifiers(productId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    // One save at a time, in order — a field left then a row removed at once are two saves, and the
    // later one must land last.
    scope: { id: `eu-identifiers-${productId}` },
    mutationFn: (ids: EuIdentifiers) => {
      if (productId == null) throw new Error('productId is null');
      return setEuIdentifiers(productId, ids);
    },
    onSuccess: (result) => {
      if (productId != null) {
        queryClient.setQueryData(euIdentifiersKey(productId), result);
        // The save bumps the product's updated_at — keep the editor's If-Match token current.
        void queryClient.invalidateQueries({ queryKey: ['product-edit', productId] });
      }
    },
  });
}

/** The product's stored {country: classification} map (editor tier — always fresh). */
export function useSubmissionPathways(productId: number | null) {
  return useQuery<SubmissionPathways>({
    queryKey:
      productId == null
        ? ['product-submission-pathways', 'none']
        : submissionPathwaysKey(productId),
    queryFn: () => {
      if (productId == null) throw new Error('productId is null');
      return getSubmissionPathways(productId);
    },
    enabled: productId != null,
    staleTime: 0,
  });
}

/**
 * Persist the country/classification map. Writes the response back to the
 * cache (so the concurrency token advances) and invalidates the products list
 * because the finder-eligibility of this product may have changed.
 */
export function useSetSubmissionPathways(productId: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pathways: Record<string, string>) => {
      if (productId == null) throw new Error('productId is null');
      return setSubmissionPathways(productId, pathways);
    },
    onSuccess: (result) => {
      if (productId != null) {
        queryClient.setQueryData(submissionPathwaysKey(productId), result);
        void queryClient.invalidateQueries({ queryKey: ['products'] });
        // A classification save bumps the product's updated_at; refresh the
        // edit snapshot so the edit page's If-Match token stays current
        // and its next autosave doesn't false-conflict (409).
        void queryClient.invalidateQueries({ queryKey: ['product-edit', productId] });
        void queryClient.invalidateQueries({
          queryKey: ['predicate-availability', productId],
        });
      }
    },
  });
}
